import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@store/store";

import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { SendEOTApi } from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  getFileUploadSignedURLThunk,
  getFileSignedURLThunk,
  getDeleteSignedURLThunk,
  deleteFileByIdThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import { deleteFileOnAwsAPI } from "../../../utils/s3Upload";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import { PictureAsPdf, VideoFile } from "@mui/icons-material";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import VerifiedUserSharpIcon from "@mui/icons-material/VerifiedUserSharp";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { productionCheck } from "@components/buttonGroupView/utils";

const RequestExtension = ({
  isRequestExtensionOpen,
  setIsRequestExtensionOpen,
  applicationId,
  provisionalExpiryDate,
  onSuccess,
}: {
  isRequestExtensionOpen: boolean;
  setIsRequestExtensionOpen: (open: boolean) => void;
  applicationId: number;
  provisionalExpiryDate?: string | null;
  onSuccess?: () => void; // NEW
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [uploading, setUploading] = useState<boolean>(false);
  const [remarks, setRemarks] = useState("");
  const [extensionTill, setExtensionTill] = useState<string>("");
  const [openExtensionDateModal, setOpenExtensionDateModal] =
    useState<boolean>(false);
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState<boolean>(false);

  const [mediaFiles, setMediaFiles] = useState<any[]>([]);
  const [filePreviews, setFilePreviews] = useState<
    {
      file: File;
      preview: string;
      uploaded: boolean;
      data?: any;
      signedUrl?: string;
    }[]
  >([]);

  const parsedProvisionalExpiryDate = provisionalExpiryDate
    ? dayjs(provisionalExpiryDate)
    : null;
  const parsedExtensionDate = extensionTill ? dayjs(extensionTill) : null;
  const isExtensionDateInvalid = Boolean(
    parsedExtensionDate &&
    parsedProvisionalExpiryDate?.isValid() &&
    parsedExtensionDate.isBefore(parsedProvisionalExpiryDate, "day"),
  );

  const resetRequestExtensionForm = () => {
    setRemarks("");
    setExtensionTill("");
    setMediaFiles([]);
    setFilePreviews([]);
    setOpenExtensionDateModal(false);
  };

  // Sanitize input to prevent script injection
  const sanitizeInput = (value: string) => {
    return value
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/<[^>]*>/g, "");
  };

  const handleUploadFile = async (
    file: File,
    documentType: "video" | "image" | "pdf",
    previewIndex: number,
  ) => {
    setUploading(true);
    try {
      if (file?.size > 21 * 1024 * 1024) {
        toast.error("File size must be less than 20 MB");
        setFilePreviews((prev) =>
          prev.filter((_, idx) => idx !== previewIndex),
        );
        return;
      }
      const urlRes = await dispatch(
        genericUploadThunk({
          file,
          moduleType: "access",
        }),
      );
      if (!urlRes?.payload?.success) {
        const urlResDetails =
          urlRes?.payload?.details || urlRes?.payload?.response?.data?.details;
        toast.error(
          Array.isArray(urlResDetails)
            ? urlResDetails?.[0]
            : typeof urlResDetails === "string"
              ? urlResDetails
              : "Something Went Wrong!",
        );
        setFilePreviews((prev) =>
          prev.filter((_, idx) => idx !== previewIndex),
        );
        return false;
      }
      if (!productionCheck) {
        const uploadSuccess = await dispatch(
          uploadToS3Thunk({
            signedUrl: urlRes.payload.data.signedUrl,
            file,
            contentType: file.type,
          }),
        ).unwrap();
        if (!uploadSuccess) throw new Error("File upload to S3 failed");
      }
      // await dispatch(
      //   postSaveUploadedFileThunk({
      //     application_id: applicationId,
      //     document_type: documentType,
      //     fileName: urlRes.payload.data.fileName,
      //     file_name: file.name,
      //     originalFileName: urlRes.payload.data.fileName,
      //     objectKey: urlRes.payload.data.objectKey,
      //     moduletype: "EOT_REQUEST",
      //     isUndertaking: false,
      //   }),
      // );

      const uploadedData = {
        fileName: file?.name,
        objectKey: urlRes?.payload?.data?.objectKey,
        document_type: documentType,
        document_name: urlRes?.payload?.data?.fileName,
        moduletype: "EOT_REQUEST",
      };

      // Fetch signed URL for preview
      let signedUrl = "";
      try {
        const signedUrlRes = await dispatch(
          getFileSignedURLThunk({
            document_name: urlRes.payload.data.fileName,
          }),
        );
        signedUrl = signedUrlRes?.payload?.data?.signedUrl || "";
      } catch (error) {
        console.error("Failed to fetch signed URL for preview:", error);
      }

      // Update preview with uploaded status and signed URL
      setFilePreviews((prev) =>
        prev.map((item, idx) =>
          idx === previewIndex
            ? { ...item, uploaded: true, data: uploadedData, signedUrl }
            : item,
        ),
      );

      // Add to mediaFiles
      setMediaFiles((prev) => [...prev, uploadedData]);
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload file");
      // Remove from preview on error
      setFilePreviews((prev) => prev.filter((_, idx) => idx !== previewIndex));
    }
    setUploading(false);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Check if adding these files would exceed the limit
    const remainingSlots = 4 - filePreviews.length;
    if (remainingSlots <= 0) {
      toast.error("Maximum 4 files allowed");
      e.target.value = "";
      return;
    }

    // Limit to remaining slots
    const filesToUpload = files.slice(0, remainingSlots);

    // Validate file sizes (20MB = 20 * 1024 * 1024 bytes)
    const maxSize = 21 * 1024 * 1024;
    const invalidFiles = filesToUpload.filter((file) => file.size > maxSize);

    if (invalidFiles.length > 0) {
      toast.error(
        `File size must not exceed 20MB. ${invalidFiles.length} file(s) rejected.`,
      );
      e.target.value = "";
      return;
    }

    if (filesToUpload.length < files.length) {
      toast.warning(
        `Only ${remainingSlots} more file(s) can be uploaded (maximum 4 total)`,
      );
    }

    // Create previews for each file
    filesToUpload.forEach((file) => {
      // Determine document type based on file type
      let documentType: "video" | "image" | "pdf" = "image";
      if (file.type === "application/pdf") {
        documentType = "pdf";
      } else if (file.type.startsWith("video/")) {
        documentType = "video";
      } else if (file.type.startsWith("image/")) {
        documentType = "image";
      }

      setFilePreviews((prev) => {
        const newPreviews = [...prev, { file, preview: "", uploaded: false }];
        const newIndex = prev.length;
        handleUploadFile(file, documentType, newIndex);
        return newPreviews;
      });
    });

    // Reset input
    e.target.value = "";
  };

  const handleRemoveFile = async (index: number) => {
    const fileToRemove = filePreviews[index];

    // Only attempt API deletion if file is uploaded and has data
    if (fileToRemove.uploaded && fileToRemove.data) {
      try {
        // 1. Get delete signed URL
        const deleteSignURL = await dispatch(
          getDeleteSignedURLThunk(fileToRemove.data.document_name),
        );
        const signedUrl = deleteSignURL?.payload?.data?.signedUrl;
        if (deleteSignURL.meta?.requestStatus === "fulfilled" && signedUrl) {
          // 2. Delete from S3
          await deleteFileOnAwsAPI(signedUrl);
        }
      } catch (error) {
        toast.error("Failed to delete file from server");
        return;
      }
      // Remove from mediaFiles
      setMediaFiles((prev) =>
        prev.filter((item) => item.fileName !== fileToRemove.data.fileName),
      );
    }
    // Remove from previews
    setFilePreviews((prev) => prev.filter((_, idx) => idx !== index));
  };

  const sendEOTdAction = async () => {
    try {
      if (!extensionTill) {
        toast.error("Please select extension date.");
        return;
      }
      if (isExtensionDateInvalid) {
        toast.error(
          "Extension date cannot be prior to provisional expiry date.",
        );
        return;
      }
      if (mediaFiles.length === 0) {
        toast.error("Please upload at least one document.");
        return;
      }
      const eotPayload = {
        application_id: applicationId,
        requested_extension_date: dayjs(extensionTill)?.format("DD-MM-YYYY"),
        extension_reason: remarks,
        media: mediaFiles,
      };

      const res = await dispatch(SendEOTApi({ payload: eotPayload }));
      if (res?.payload?.success) {
        toast.success(res?.payload?.message);
        onSuccess?.();
        resetRequestExtensionForm();
        setIsRequestExtensionOpen(false);
        setIsSuccessPopupOpen(true);
      } else {
        toast.error(res?.payload?.message || res?.payload?.errors?.[0]);
      }
    } catch (error: any) {
      console.error("Error sending EOT request:", error);
      toast.error(error?.message || "An error occurred");
    }
  };

  return (
    <>
      {/* Request Extension Dialog */}
      <Dialog
        open={isRequestExtensionOpen}
        onClose={() => {
          resetRequestExtensionForm();
          setIsRequestExtensionOpen(false);
        }}
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            overflow: "hidden",
          },
        }}
        fullWidth
      >
        <DialogTitle className="dialog-title">
          Provisional NOC Extension Request
          <IconButton
            aria-label="close"
            onClick={() => {
              resetRequestExtensionForm();
              setIsRequestExtensionOpen(false);
            }}
            className="close-icon"
            sx={{
              position: "absolute",
              right: 19,
              top: 12,
              aspectRatio: "1/1",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="dialog-content">
          <Box mt={1}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontSize: 14, fontWeight: "400", color: "#878787" }}
            >
              Extension Required Till
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                open={openExtensionDateModal}
                onOpen={() => setOpenExtensionDateModal(true)}
                onClose={() => setOpenExtensionDateModal(false)}
                format="DD/MM/YYYY"
                value={extensionTill ? dayjs(extensionTill) : null}
                onChange={(newValue) => {
                  if (newValue) {
                    setExtensionTill(newValue.format("YYYY-MM-DD"));
                  } else {
                    setExtensionTill("");
                  }
                }}
                minDate={dayjs()}
                maxDate={dayjs().add(84, "month")}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    placeholder: "Select Extension Date",
                    sx: {
                      background: "#fff",
                      borderRadius: "8px",
                    },
                    inputProps: {
                      readOnly: true,
                    },
                    onClick: () => {
                      setOpenExtensionDateModal(true);
                    },
                  },
                  popper: { sx: { zIndex: 20000 } },
                }}
              />
            </LocalizationProvider>
            {isExtensionDateInvalid && (
              <Typography
                variant="caption"
                sx={{ color: "#d32f2f", mt: 1, display: "block" }}
              >
                Extension date cannot be prior to provisional expiry date.
              </Typography>
            )}
          </Box>

          <Box mt={3}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontSize: 14, fontWeight: "400", color: "#878787" }}
            >
              Reason for Extension<span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder="Enter your reason"
              variant="outlined"
              sx={{ background: "#fff", borderRadius: "8px" }}
              value={remarks}
              onChange={(e) => setRemarks(sanitizeInput(e.target.value))}
            />
          </Box>

          <Box mt={3}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontSize: 14, fontWeight: "400", color: "#878787" }}
            >
              Upload documents/screenshots<span className="required">*</span>
            </Typography>
            <Typography fontSize={12} color="text.secondary" sx={{ mb: 1.5 }}>
              <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                <li>
                  <strong>
                    Current construction status report (self-certified) of
                    proposed Amenities - Mandatory
                  </strong>
                  <br />
                  (Percentage completion, works done so far and pending works
                  with photographs)
                </li>

                <li style={{ marginTop: "8px" }}>
                  <strong>
                    Land acquisition / conversion status proof (if delay is due
                    to land) - If applicable
                  </strong>
                  <br />
                  (Copy of land acquisition proceedings, conversion
                  application/approval, mutation records etc.)
                </li>

                <li style={{ marginTop: "8px" }}>
                  <strong>
                    Updated drawings (if layout has changed from originally
                    approved plan) - If applicable
                  </strong>
                  <br />
                  (Location plan, layout plan, drainage plan as per current
                  proposed design
                  <br />
                  If applicable)
                </li>
              </ul>
            </Typography>
            <Box
              sx={{
                border: "1px dashed #ccc",
                borderRadius: "8px",
                p: 2,
                backgroundColor: "#faf8ff",
                minHeight: "100px",
              }}
            >
              <Button
                variant="outlined"
                component="button"
                onClick={() => document.getElementById("photo-input")?.click()}
                disabled={filePreviews.length >= 4}
                sx={{
                  borderRadius: "24px",
                  textTransform: "none",
                  borderColor: filePreviews.length >= 4 ? "#ccc" : "#4b2aad",
                  color: filePreviews.length >= 4 ? "#ccc" : "#4b2aad",
                  fontWeight: 500,
                  px: 2,
                  mb: filePreviews.length > 0 ? 2 : 0,
                  "&:hover": {
                    borderColor: filePreviews.length >= 4 ? "#ccc" : "#4b2aad",
                  },
                }}
                startIcon={<i className="fa fa-upload" />}
              >
                Upload Files{" "}
                {filePreviews.length > 0 && `(${filePreviews.length}/4)`}
                <input
                  id="photo-input"
                  type="file"
                  accept="image/*,application/pdf,video/*"
                  style={{ display: "none" }}
                  multiple
                  onChange={handlePhotoChange}
                />
              </Button>

              {/* Display file previews in grid */}
              {filePreviews.length > 0 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(100px, 1fr))",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {filePreviews.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "relative",
                        borderRadius: "8px",
                        overflow: "hidden",
                        border: "1px solid #ddd",
                        aspectRatio: "1",
                        backgroundColor: "#fff",
                        cursor:
                          item.uploaded && item.signedUrl
                            ? "pointer"
                            : "default",
                      }}
                      onClick={() => {
                        if (item.uploaded && item.signedUrl) {
                          window.open(
                            item.signedUrl,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }
                      }}
                    >
                      {/* Preview Image/PDF/Video */}
                      {item.file.type.startsWith("image/") ? (
                        item.signedUrl ? (
                          <img
                            src={item.signedUrl}
                            alt={item.file.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            <i
                              className="fa fa-image"
                              style={{ fontSize: "2rem", color: "#757575" }}
                            />
                          </Box>
                        )
                      ) : item.file.type.startsWith("video/") ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          <i
                            className="fa fa-video-camera"
                            style={{ fontSize: "2rem", color: "#1976d2" }}
                          />
                          <VideoFileIcon
                            sx={{ fontSize: "2rem", color: "#3a1673" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 1,
                              fontSize: "0.7rem",
                              textAlign: "center",
                              px: 1,
                            }}
                          >
                            {item.file.name}
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          <i
                            className="fa fa-file-pdf"
                            style={{ fontSize: "2rem", color: "#d32f2f" }}
                          />
                          <PictureAsPdf
                            sx={{ fontSize: "2rem", color: "#d32f2f" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 1,
                              fontSize: "0.7rem",
                              textAlign: "center",
                              px: 1,
                            }}
                          >
                            {item.file.name}
                          </Typography>
                        </Box>
                      )}

                      {/* Upload status overlay */}
                      {!item.uploaded && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <i className="fa fa-spinner fa-spin" />
                        </Box>
                      )}

                      {/* Delete button */}
                      {item.uploaded && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(index);
                          }}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            backgroundColor: "rgba(255,255,255,0.9)",
                            width: "24px",
                            height: "24px",
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,1)",
                            },
                          }}
                          size="small"
                        >
                          <CloseIcon
                            sx={{ fontSize: "1rem", color: "#d32f2f" }}
                          />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, background: "#faf8ff" }}>
          <Button
            onClick={() => {
              resetRequestExtensionForm();
              setIsRequestExtensionOpen(false);
            }}
            variant="outlined"
            sx={{
              borderRadius: "24px",
              borderColor: "#4b2aad",
              color: "#4b2aad",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={sendEOTdAction}
            sx={{
              borderRadius: "24px",
              backgroundColor:
                remarks.trim() &&
                mediaFiles.length > 0 &&
                extensionTill &&
                !isExtensionDateInvalid
                  ? "#3A1C73"
                  : "#ccc",
              textTransform: "none",
              fontWeight: 500,
              width: "110px",
              px: 4,
              "&:hover": {
                backgroundColor:
                  remarks.trim() &&
                  mediaFiles.length > 0 &&
                  extensionTill &&
                  !isExtensionDateInvalid
                    ? "#3A1C73"
                    : "#ccc",
              },
            }}
            disabled={
              !(
                remarks.trim() &&
                mediaFiles.length > 0 &&
                extensionTill &&
                !isExtensionDateInvalid
              ) || uploading
            }
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isSuccessPopupOpen}
        onClose={() => setIsSuccessPopupOpen(false)}
        maxWidth={false}
        fullWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            width: "400px",
            maxWidth: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            position: "relative",
            pb: 1,
          }}
        >
          Provisional NOC Extension Request
          <IconButton
            aria-label="close"
            onClick={() => setIsSuccessPopupOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            borderBottom: "none",
            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "left", mb: 3 }}>
            <VerifiedUserSharpIcon sx={{ fontSize: 64, color: "#43B864" }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", mb: 1 }}>
            Request Submitted Successfully
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your extension request has been submitted and is now under review.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setIsSuccessPopupOpen(false)}
            sx={{
              borderRadius: "24px",
              backgroundColor: "#3A1C73",
              textTransform: "none",
              fontWeight: 500,
              width: "100%",
              mb: 1,
              "&:hover": { backgroundColor: "#2a145c" },
            }}
          >
            OK, Got it.
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequestExtension;
